import { Readable, type Writable } from "node:stream";

import { parseCliArgs, describeCliProtocol, type CliRequest, type CommandRequest } from "./protocol.js";
import { handleAtomList, handleAtomShow, handleAtomUpsert } from "../commands/atom-command.js";
import {
  handleBoardCreate,
  handleBoardJudge,
  handleBoardList,
  handleBoardShow,
} from "../commands/board-command.js";
import { handleCellList, handleCellUpsert } from "../commands/cell-command.js";
import { handleCompact } from "../commands/compact-command.js";
import { handleContextShow } from "../commands/context-command.js";
import {
  handleDictGet,
  handleDictGetAll,
  handleDictSet,
  handleDictUnset,
} from "../commands/dict-command.js";
import { handleLineSelect, handleLineShow } from "../commands/line-command.js";
import { handleProofList, handleProofRecord } from "../commands/proof-command.js";
import { handleSearchNext, handleSearchShow } from "../commands/search-command.js";
import { CliError, errorData } from "../errors/index.js";
import { resolveExecutionContext } from "../runtime/context.js";
import { writeJson, writeText } from "../runtime/output.js";
import { getPackageVersion } from "../runtime/package-version.js";

const HELP_TEXT = `Usage:
  emergent-thinking install
  emergent-thinking board create -s demo/x
  emergent-thinking atom upsert -s demo/x -p @tip -a problem/core -k problem :Define the core problem
  emergent-thinking cell upsert -s demo/x -p @tip -f problem/core -e targets -t goal/main
  emergent-thinking line select -s demo/x -p @tip -a path/mainline
  emergent-thinking proof record -s demo/x -p @tip -t path/mainline -v pass :Prove the path
  emergent-thinking search show -s demo/x
  emergent-thinking compact -s demo/x

Machine modes:
  emergent-thinking '{"cmd":"board.create","s":"demo/x"}'
  emergent-thinking '["atom","upsert","demo/x","@tip","problem/core","problem",":Define the problem"]'
  emergent-thinking '[["atom","upsert","demo/x","@tip","goal/main","goal",":Reach the target."],["line","select","demo/x","@tip","path/mainline"]]'

Global options:
  --cwd <path>   Resolve project context from another directory
  --json         Force JSON output when a command defaults to text
  --pretty       Pretty-print JSON output
  --help         Show help
  --version      Show version

Install:
  emergent-thinking install [options]
  emergent-thinking upgrade [options]`;

type RunCliOptions = {
  cwd: string;
  env: NodeJS.ProcessEnv;
  stderr: Writable;
  stdin: Readable;
  stdout: Writable;
};

type CommandResult = {
  data: unknown;
  text?: string;
};

export async function runCli(argv: string[], io: RunCliOptions) {
  try {
    if (argv[0] === "install") {
      const { runInstallCommand } = await import("./install.js");
      return await runInstallCommand(argv.slice(1), io);
    }
    if (argv[0] === "upgrade") {
      const { runUpgradeCommand } = await import("./install.js");
      return await runUpgradeCommand(argv.slice(1), io);
    }

    const parsed = parseCliArgs(argv);
    if (parsed.version) {
      await writeJson(io.stdout, { ok: true, data: { version: await getPackageVersion() } }, {
        pretty: parsed.pretty,
      });
      return 0;
    }

    if (parsed.help || parsed.request === undefined) {
      const payload = { help: HELP_TEXT, protocol: describeCliProtocol() };
      if (parsed.json) {
        await writeJson(io.stdout, { ok: true, data: payload }, { pretty: parsed.pretty });
      } else {
        await writeText(io.stdout, HELP_TEXT);
      }
      return 0;
    }

    const context = resolveExecutionContext(parsed.cwd ?? io.cwd);
    const result = await dispatchRequest(parsed.request, context, io.stdin);

    if (result.text !== undefined && !parsed.json) {
      await writeText(io.stdout, result.text);
      return 0;
    }

    await writeJson(io.stdout, { ok: true, data: result.data }, { pretty: parsed.pretty });
    return 0;
  } catch (error) {
    await writeJson(
      io.stderr,
      {
        ok: false,
        error: errorData(error),
        version: await getPackageVersion(),
      },
      { pretty: true },
    );
    return 1;
  }
}

async function dispatchRequest(
  request: CliRequest,
  context: ReturnType<typeof resolveExecutionContext>,
  stdin: Readable,
): Promise<CommandResult> {
  if (!isRequestSequence(request)) {
    return await dispatchCommand(request, context, stdin);
  }

  ensureBatchInlineBodies(request);
  let finalResult: CommandResult | undefined;
  for (const entry of request) {
    finalResult = await dispatchCommand(entry, context, Readable.from([]));
  }
  if (finalResult === undefined) {
    throw new CliError("INVALID_BATCH", "Batch request produced no executable commands");
  }

  const batchResult: CommandResult = {
    data: {
      count: request.length,
      final: finalResult.data,
    },
  };
  if (finalResult.text !== undefined) {
    batchResult.text = finalResult.text;
  }
  return batchResult;
}

function isRequestSequence(request: CliRequest): request is readonly CommandRequest[] {
  return Array.isArray(request);
}

function ensureBatchInlineBodies(requests: readonly CommandRequest[]) {
  const requiresInlineSpec = requests.some((request) =>
    (request.cmd === "atom.upsert" || request.cmd === "proof.record") &&
    request.b === undefined,
  );

  if (requiresInlineSpec) {
    throw new CliError(
      "INVALID_BATCH",
      'Batch requests require inline body specs for "atom.upsert" and "proof.record"; use ":", "file:", or "http(s):" content.',
    );
  }
}

async function dispatchCommand(
  request: CommandRequest,
  context: ReturnType<typeof resolveExecutionContext>,
  stdin: Readable,
): Promise<CommandResult> {
  switch (request.cmd) {
    case "context.show":
      return { data: handleContextShow(context) };
    case "board.create":
      return { data: await handleBoardCreate(context, request) };
    case "board.show":
      return { data: await handleBoardShow(context, request) };
    case "board.judge":
      return { data: await handleBoardJudge(context, request) };
    case "board.list":
      return { data: await handleBoardList(context, request) };
    case "atom.upsert":
      return { data: await handleAtomUpsert(context, request, stdin) };
    case "atom.list":
      return { data: await handleAtomList(context, request) };
    case "atom.show":
      return { data: await handleAtomShow(context, request) };
    case "cell.upsert":
      return { data: await handleCellUpsert(context, request) };
    case "cell.list":
      return { data: await handleCellList(context, request) };
    case "line.select":
      return { data: await handleLineSelect(context, request) };
    case "line.show":
      return { data: await handleLineShow(context, request) };
    case "proof.record":
      return { data: await handleProofRecord(context, request, stdin) };
    case "proof.list":
      return { data: await handleProofList(context, request) };
    case "search.next":
      return { data: await handleSearchNext(context, request) };
    case "search.show":
      return { data: await handleSearchShow(context, request) };
    case "compact": {
      const result = await handleCompact(context, request);
      return {
        data: result,
        text: result.compact,
      };
    }
    case "dict.get":
      return { data: await handleDictGet(context, request) };
    case "dict.getAll":
      return { data: await handleDictGetAll(context, request) };
    case "dict.set":
      return { data: await handleDictSet(context, request) };
    case "dict.unset":
      return { data: await handleDictUnset(context, request) };
  }
}
